import ChalkySticks from '@chalkysticks/sdk';

// Type definitions for alert options
export interface AlertOption {
	callback?: () => void;
	text: string;
	type?: string;
	value: any;
}

/**
 * Common alert templates for quick use
 */
export const AlertTemplate = {
	confirmDelete: {
		message: 'Are you sure you want to delete this item?',
		title: 'Delete',
	},
	error: {
		message: 'Something went wrong. Please try again later.',
		title: 'Error',
	},
};

/**
 * AlertManager
 *
 * A utility class for showing alerts, confirmations, and prompts using the ChalkySticks event bus.
 * This provides a simple API for triggering dialogs from anywhere in the app. The dialogs are
 * rendered by a ChalkyUtilityAlertPrompt component listening on the event bus (see App.vue wiring).
 *
 * @example
 * // Import and use statically
 * import AlertManager from '@/Manager/Alert';
 * await AlertManager.alert('Something happened!', 'Notice');
 *
 * // Use as Vue prototype in a component
 * this.$alert.alert('Hello from prototype!');
 *
 * // Confirm dialog
 * const confirmed = await this.$alert.confirm('Are you sure?', 'Confirm');
 * if (confirmed) {
 *   // User confirmed
 * }
 *
 * // Prompt with options
 * const value = await this.$alert.prompt('Choose:', 'Options', [
 *   { text: 'A', value: 'a' },
 *   { text: 'B', value: 'b' },
 * ]);
 *
 * // Use a template
 * await this.$alert.template('confirmDelete');
 *
 * // Register as plugin in setup.ts
 * import AlertManager from '@/Manager/Alert';
 * AlertManager.install(Vue);
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Manager
 * @project ChalkySticks
 */
export default class AlertManager {
	/**
	 * Show a basic alert dialog.
	 *
	 * @example
	 * await AlertManager.alert('Hello world!', 'Greeting');
	 *
	 * @param string message The message to display
	 * @param string title The dialog title (optional)
	 * @return Promise<void>
	 */
	public static async alert(message: string, title: string = 'Alert'): Promise<void> {
		ChalkySticks.Core.Event.Bus.dispatch('dialog:alert', {
			message,
			title,
		});
	}

	/**
	 * Show a confirmation dialog with Yes/No buttons.
	 *
	 * @example
	 * const confirmed = await AlertManager.confirm('Are you sure?', 'Confirm');
	 * if (confirmed) { ... }
	 *
	 * @param string message The message to display
	 * @param string title The dialog title (optional)
	 * @return Promise<boolean> Resolves to true if confirmed, false otherwise
	 */
	public static async confirm(message: string, title: string = 'Confirm'): Promise<boolean> {
		return new Promise((resolve) => {
			ChalkySticks.Core.Event.Bus.dispatch('dialog:confirm', {
				callback: (result: boolean) => resolve(result),
				message: message,
				title: title,
			});
		});
	}

	/**
	 * Show a prompt dialog with custom options.
	 *
	 * @example
	 * const value = await AlertManager.prompt('Choose:', 'Options', [
	 *   { text: 'A', value: 'a' },
	 *   { text: 'B', value: 'b' },
	 * ]);
	 *
	 * @param string message The message to display
	 * @param string title The dialog title (optional)
	 * @param Array<any> options Array of button options (see AlertPromptExample.vue)
	 * @return Promise<any> Resolves to the selected value
	 */
	public static async prompt(message: string, title: string = 'Prompt', options: Array<any> = []): Promise<any> {
		return new Promise((resolve) => {
			ChalkySticks.Core.Event.Bus.dispatch('dialog:prompt', {
				callback: (value: any) => resolve(value),
				message: message,
				options: options,
				title: title,
			});
		});
	}

	/**
	 * Show a template alert by key
	 *
	 * @example
	 * await AlertManager.template('error');
	 *
	 * @param string key The template key from AlertTemplate
	 * @return Promise<void>
	 */
	public static async template(key: keyof typeof AlertTemplate): Promise<void> {
		const template = AlertTemplate[key] || AlertTemplate.error; // Fallback to error template if not found

		if (template) {
			await AlertManager.alert(template.message, template.title);
		}
	}

	/**
	 * Install as a Vue prototype for $alert access
	 *
	 * @param any Vue The Vue constructor
	 * @return void
	 */
	public static install(Vue: any): void {
		Vue.prototype.$alert = AlertManager;
	}
}
